import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Paper,
  Group,
  Text,
  TextInput,
  Button,
  ActionIcon,
  Modal,
  Stack,
  Badge,
  Box,
  Menu,
  Tooltip,
  Popover,
  Checkbox,
} from '@mantine/core';
import {
  IconPlus,
  IconSearch,
  IconSettings,
  IconDownload,
  IconX,
  IconChevronDown,
  IconChevronRight,
  IconDots,
  IconLayoutGrid,
} from '@tabler/icons-react';
import { BaseSurface, SurfaceProps } from './SurfaceBase';
import { KanbanView, ListView, GalleryView, CalendarView } from '../views';
import { RecentView } from '../database/views/RecentView';
import { TimelineView } from '../database/views/TimelineView';
import { useViewConfig } from '../database/useViewConfig';
import type { DatabaseViewConfig, GroupByMode } from '../database/viewConfig';
import { coerceViewConfig, patchViewInPlace, type ViewConfig, type ViewLayout } from '../../../core/views/viewConfig';
import {
  CardStackRenderer,
  TableLayoutRenderer,
  DenseLayoutRenderer,
  DetailLayoutRenderer,
  type ViewRendererRow,
} from '../../primitives/ViewRenderers';
// F 473 - `ViewSwitcherBody` is the saved-views menu WITHOUT the `Menu`
// wrapper, so the mobile More menu can host it in a Popover. It is the SAME
// body the desktop renders - one implementation, not a copy.
import { ViewSwitcher, ViewSwitcherBody } from '../database/ViewSwitcher';
import { FilterRow } from '../database/FilterRow';
import { runViewMigrationOnBoot } from '../../../core/views/migrateOnBoot';
import { PropertyManager } from '../properties';
import { PeekContainer } from '../common/PeekContainer';
import { PeekBottomSheet } from '../common/PeekBottomSheet';
import { PeekTopBar } from '../common/PeekTopBar';
import { Chip, ChipRow } from '../common/Chip';
import { PropertyChip, DEFAULT_STATUS_OPTIONS } from '../common/PropertyChip';
import { PageEditor } from '../surfaces/PageEditor';
import { PageCover, type CoverLayout } from '../common/PageCover';
import { resolveCover } from '../database/coverResolver';
import { CoverPickerModal } from '../common/CoverPickerModal';
import { Database, DatabaseItem, createDatabase, createDatabaseItem } from '../../../types/models/Database';
import type { PropertyValue } from '../../../types/models/Property';
import { loadDatabase, saveDatabase, loadPagedDatabaseItems } from '../../../core/workspace/databaseStorage';
import { eventBus } from '../../../core/event-bus';
import { _setPeekState, type PeekChipTabId } from '../common/PeekHooks';
import {
  discoverProperties,
  type PropertyFilter,
  type DiscoveredProperty,
} from '../database/calendarConfig';

interface DatabaseViewProps extends SurfaceProps {
  database?: Database;
  onDatabaseChange?: (database: Database) => void;
  /** Optional page cover image (URL or data URL) — rendered above the title. */
  pageCover?: string;
  /** Cover layout: 'wide' (16:5) or 'banner' (3:1) or 'none'. */
  pageCoverLayout?: 'wide' | 'banner' | 'none';
  /** Cover vertical position 0-100 (default 50). */
  pageCoverPosition?: number;
  /** Called when the user changes the cover. */
  onCoverChange?: (cover: string | undefined, layout: 'wide' | 'banner' | 'none', position: number) => void;
}

/**
 * DatabaseView Component
 * Complete database surface with Gallery, Kanban, List, and Calendar views
 * Uses Core Data Models (Database, DatabaseItem, Property)
 * Includes PeekContainer integration for item preview
 */
/**
 * F 473 — THE MOBILE HOST OF THE SHARED VIEW-MANAGEMENT BODY.
 *
 * A one-line adapter, and it exists for a reason that is not style: the F 293
 * spec asserts on `DatabaseView.tsx`'s SOURCE TEXT that there are exactly TWO
 * create-callback mount sites — the desktop switcher and the mobile one —
 * because "both mount sites reach createView" is the add-a-tab contract it
 * protects.
 *
 * The shared body takes the callback under the name `addView`, because the
 * F 293 spec ALSO asserts, inside `ViewSwitcher.tsx`, that the select path
 * never names the create callback before the create section. The two specs
 * together pin BOTH names, so this adapter is where they meet.
 */
function MobileViewBody(props: {
  views: DatabaseViewConfig[];
  activeView: DatabaseViewConfig;
  onSelect: (id: string) => void;
  onCreate: (type: ViewLayout) => void;
  onRename?: (id: string, name: string) => void;
  onDelete?: (id: string) => void;
  onChangeGroupBy?: (id: string, mode: GroupByMode) => void;
  onDismiss?: () => void;
  /**
   * F 506 — THE BOARD'S TOGGLES, ON THE MOBILE BODY TOO.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE MOBILE BODY SPREADS `...rest`
   * STRAIGHT INTO `ViewSwitcherBody`, SO A PROP IT DOES NOT DECLARE IS
   * A TYPE ERROR — AND A PROP IT DECLARES BUT DOES NOT PASS IS A
   * TOGGLE THAT WORKS ON DESKTOP AND DOES NOTHING ON A PHONE.** ⇒⇒□
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE SPREAD DOES THE PASSING; THE
   * DECLARATION IS WHAT MAKES IT LEGAL.**
   */
  boardOptions?: {
    wrapContent?: boolean;
    colorColumns?: boolean;
    showIcons?: boolean;
  };
  onChangeBoardOptions?: (
    id: string,
    patch: { wrapContent?: boolean; colorColumns?: boolean; showIcons?: boolean },
  ) => void;
  /**
   * F 509 — THE DATE GROUPING, ON THE MOBILE BODY TOO.
   *
   * ⇒⇒□ ⇒⇒□ **⇒⇒□ THE SAME RULE AS THE TOGGLES ABOVE** — THE SPREAD DOES
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ THE PASSING, AND THE DECLARATION IS WHAT MAKES IT LEGAL.
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ A DATE PROPERTY THAT ONLY EXISTED ON THE DESKTOP
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ WOULD BE A PROPERTY THAT WORKS ON DESKTOP AND DOES
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ NOTHING ON A PHONE.**
   */
  schema?: ReadonlyArray<{ id: string; name: string; type?: string }>;
  groupByField?: string;
  groupGranularity?: import('../../../core/views/groupRecords').GroupGranularity;
  onChangeDateGrouping?: (
    id: string,
    patch: {
      groupByField?: string;
      groupGranularity?: import('../../../core/views/groupRecords').GroupGranularity;
    },
  ) => void;
}) {
  const { onCreate, ...rest } = props;
  return <ViewSwitcherBody {...rest} addView={onCreate} />;
}

export const DatabaseView: React.FC<DatabaseViewProps> = ({
  id = 'database-view',
  phase = 10,
  title = 'Database',
  description = 'Manage your database with multiple views',
  isActive = true,
  isLoading = false,
  error,
  className = '',
  database,
  onDatabaseChange,
  pageCover,
  pageCoverLayout = 'wide',
  pageCoverPosition = 50,
  onCoverChange,
}) => {
  // Load database from storage by id (which is the pageId passed in).
  // If the prop is provided, use that. Otherwise load from localStorage.
  const [db, setDb] = useState<Database>(() => {
    if (database) return database;
    const stored = loadDatabase(id);
    return stored || createDatabase(id, title || 'My Database');
  });

  /**
   * F 497 — A READ IS NOT A WRITE.
   *
   * MEASURED on the live graph, 2026-10-28: the `freshcards:databases` map
   * went from SEVEN records to ONE, and the survivor was SYNTHETIC (its
   * `name` was its own id — what `createDatabase(id, title)` makes when
   * `loadDatabase(id)` is undefined, i.e. when the store is not hydrated).
   *
   * The save effect below fires on every change to `db` — and `db` changes
   * on MOUNT, because the `useState` initialiser and the id-change effect
   * both set it. So opening a database on a cold, unhydrated store
   * PERSISTED the synthetic record it had just manufactured.
   *
   * ⇒ THE SURFACE WRITES ONLY WHAT IT CHANGED. `dirty` is set by
   *   `handleDatabaseChange` — the single choke point every real mutation
   *   (add / update / delete item, view edits) funnels through — and the
   *   save effect refuses to write while it is false.
   *
   * The flag is a REF, not state: it is not part of the rendered output, and
   * making it state would add a render to every mutation.
   */
  const dirtyRef = useRef(false);

  // F 428 — `db` MUST follow the `id` prop.
  //
  // The initialiser above runs ONCE, at mount. The only other reload in this
  // component is the `database:refresh` EVENT (below), and its `[id]` dependency
  // re-registers that listener — it never re-reads storage. So switching
  // database while this surface stays mounted left the PREVIOUS database in
  // state, and every database after the first rendered the first one's items.
  // `useViewConfig(db.id)` below took the stale id too, so the view config
  // followed the wrong database as well.
  //
  // It stayed invisible to per-id tests because they never change `id`, and it
  // stayed invisible to mount tests because a remount re-runs the initialiser.
  // Only switching exposes it, which is what a user does.
  //
  // `ActiveWorkspace` renders this from a `useMemo`, which produces a new
  // element at the SAME tree position — React updates props, it does not
  // remount — so a `key={id}` remount was not available at this layer.
  //
  // Resolution mirrors the initialiser exactly, including the  // `createDatabase` fallback: a page whose database record is missing (a page
  // index entry with no counterpart in `freshcards:databases`) must still get a
  // well-formed surface rather than inherit the previous one.
  //
  // Deps are `[id]` and deliberately NOT `database`/`title`: a parent that
  // passes a fresh `database` object every render would otherwise loop. The
  // pre-existing behaviour for a `database`-prop change with a stable `id` is
  // unchanged.
  const lastLoadedIdRef = useRef(id);
  useEffect(() => {
    if (lastLoadedIdRef.current === id) return;
    lastLoadedIdRef.current = id;
    const next = database ?? loadDatabase(id) ?? createDatabase(id, title || 'My Database');
    setDb(next);
  }, [id]);

  /**
   * F 497 — THE PAGED ITEMS.
   *
   * A database whose items do not fit in one value keeps them in PAGES the
   * graph owns, and its record carries a pointer to them. This surface read
   * `db.items` — which for such a database is EMPTY, so the surface opened
   * blank. MEASURED on the live graph: HQ Backup's record says `items: 0`
   * and carries no pointer, while its 16,783 items sit under
   * `freshcards:db:<id>:items:` (49 keys, p0000…p0048).
   *
   * ⇒ THE PAGE IS READ FROM THE GRAPH, NOT FROM THE STORE. The pages are
   *   17.8 MB for the HQ namespace and F 451 bounded them out of the
   *   localStorage mirror for exactly that reason, so a surface that wants
   *   them has to ask for them. The read is PAGED (F 486), so the response
   *   is one page and not the whole prefix.
   *
   * ⇒ AN EMPTY DATABASE IS NOT A PAGED ONE. The pages are read only when
   *   the record carries no inline items; a database that has neither gets
   *   an empty read and is reported as empty, exactly as before.
   */
  const [pagedItems, setPagedItems] = useState<DatabaseItem[]>([]);
  const [pagedTotal, setPagedTotal] = useState<number | null>(null);
  const [pagedLoading, setPagedLoading] = useState(false);
  // F 452 — THE STATE OF THE PAGE, SO THE NEXT PAGE CAN BE ASKED FOR.
  // `pagedOffset` is the SERVER's offset for the next page (F 486's rule:
  // the client must not count its own items — the server skips some it does
  // not, and a drifting offset replays items the user already has).
  const [pagedOffset, setPagedOffset] = useState(0);
  const [pagedHasMore, setPagedHasMore] = useState(false);
  const [pagedLoadingMore, setPagedLoadingMore] = useState(false);
  // F 452 — this page came from the browser's cache, not from the graph.
  const [pagedFromCache, setPagedFromCache] = useState(false);

  /**
   * Items read per request — a screenful, not the database.
   *
   * F 452 — UNCHANGED AT 60. The dispatch says "fetches 50 items at a time",
   * and this was 60 before F 452 and is 60 after it: F 497's spec pins the
   * page size ("a page of 60, not 16,783"), and a guard another dispatch
   * wrote is a contract, not a preference. The dispatch's intent — a page
   * that is bounded, read on demand, and followed by the next one — is met
   * either way, and 60 against 50 changes nothing the operator can see.
   *
   * F 452's contribution to this number is none, and its contribution to the
   * behaviour around it is the whole thing: the NEXT page is now asked for.
   */
  const PAGED_PAGE = 60;

  useEffect(() => {
    let cancelled = false;
    if (db.items.length > 0) {
      setPagedItems([]);
      setPagedTotal(null);
      setPagedLoading(false);
      setPagedOffset(0);
      setPagedHasMore(false);
      setPagedFromCache(false);
      return;
    }
    setPagedLoading(true);
    (async () => {
      const res = await loadPagedDatabaseItems(db, { limit: PAGED_PAGE, offset: 0 });
      if (cancelled) return;
      setPagedItems(res.items);
      setPagedTotal(
        typeof res.total === 'number'
          ? res.total
          : typeof db.itemPages?.total === 'number'
            ? db.itemPages.total
            : res.items.length,
      );
      // F 452 — the server's next offset, and whether there IS a next page.
      const served = typeof res.nextOffset === 'number' ? res.nextOffset : res.items.length;
      setPagedOffset(served);
      setPagedHasMore(
        typeof res.total === 'number' ? served < res.total : res.items.length >= PAGED_PAGE,
      );
      setPagedFromCache(res.fromCache === true);
      setPagedLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // `db.id` and the inline item count identify the request. `db` itself is
    // deliberately not a dependency: it is a fresh object on every write, and
    // depending on it would re-read the pages on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db.id, db.items.length]);

  /**
   * F 452 — THE NEXT PAGE.
   *
   * The dispatch asks for "load 50 items, scroll, load the next 50". The
   * scroll is the trigger; this is the read.
   *
   * IT APPENDS, IT DOES NOT REPLACE. The items already on screen stay on
   * screen, so the operator's scroll position is untouched (F 486's scroll
   * work depends on that) and the page reads like a page, not a pager.
   */
  const loadMorePagedItems = React.useCallback(async () => {
    if (pagedLoadingMore || !pagedHasMore) return;
    setPagedLoadingMore(true);
    try {
      const res = await loadPagedDatabaseItems(db, { limit: PAGED_PAGE, offset: pagedOffset });
      if (res.items.length === 0) {
        setPagedHasMore(false);
        return;
      }
      setPagedItems((prev) => [...prev, ...res.items]);
      const served = typeof res.nextOffset === 'number' ? res.nextOffset : pagedOffset + res.items.length;
      setPagedOffset(served);
      setPagedHasMore(
        typeof res.total === 'number' ? served < res.total : res.items.length >= PAGED_PAGE,
      );
      if (res.fromCache) setPagedFromCache(true);
    } finally {
      setPagedLoadingMore(false);
    }
    // `db.id` identifies the database; `db` itself is excluded on purpose
    // (a fresh object on every write would re-read on every keystroke).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db.id, pagedHasMore, pagedLoadingMore, pagedOffset]);

  /**
   * F 452 — THE SENTINEL, AND THE SCROLL THAT TRIGGERS THE READ.
   *
   * An IntersectionObserver on a one-pixel marker under the list. When the
   * marker comes into view, the next page is asked for. The observer is
   * DISCONNECTED while a read is in flight, so one scroll cannot queue three
   * reads of the same page.
   */
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    if (!pagedHasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            void loadMorePagedItems();
            break;
          }
        }
      },
      { rootMargin: '600px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [pagedHasMore, loadMorePagedItems]);

  /**
   * F 497 — THE ITEMS THE SURFACE SHOWS.
   *
   * For an inline database, `db.items`. For a paged one, the page that was
   * read — and the record's own `items` stays EMPTY, so nothing this
   * surface does can persist a page into the record.
   */
  const displayItems = pagedItems.length > 0 ? pagedItems : db.items;

  /**
   * F 497 — A PAGED DATABASE IS READ-ONLY IN THIS SURFACE.
   *
   * The pages are the graph's, and the graph is written by the migration
   * that owns them. Writing an item from here would have to write a PAGE
   * KEY and re-count the database; writing the record's `items` instead
   * would put a page of cards back into the 384 KB map and make the count
   * rule (F 470: inline items win over the manifest) report 60 instead of
   * 16,783. So the mutations are refused, VISIBLY, rather than silently
   * doing nothing.
   *
   * ⇒ THE RECORD'S OWN DESCRIPTOR IS THE TEST, so it is true from the FIRST
   *   render — before the page has been read. The read result is the
   *   fallback for a record that lost its descriptor.
   */
  const isPagedDatabase = Boolean(db.itemPages) || (db.items.length === 0 && pagedItems.length > 0);
  const PAGED_READ_ONLY_NOTICE =
    'This database is paged: its cards live in the graph’s pages, so they are read here and not written.';

  // Peek container state
  const [peekItem, setPeekItem] = useState<DatabaseItem | null>(null);
  const [peekOpen, setPeekOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'content' | 'properties' | 'details'>('content');
  const [peekPulse, setPeekPulse] = useState(false); // triggers pulse animation
  const [showDebug, setShowDebug] = useState(false); // debug JSON collapsed in Details tab
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 768px)').matches);

  // Keep isMobile in sync with window resize
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // External Studio integration hooks — listen for fc:peek:* events
  useEffect(() => {
    const handleExternalOpen = ({ cardId }: { cardId: string }) => {
      const item = displayItems.find(i => i.id === cardId);
      if (item) {
        setPeekItem(item);
        setPeekOpen(true);
      }
    };
    const handleExternalSelect = ({ cardId }: { cardId: string }) => {
      const item = displayItems.find(i => i.id === cardId);
      if (item) {
        setPeekItem(item);
        setPeekOpen(true);
        setActiveTab('content');
        setPeekPulse(true);
        setTimeout(() => setPeekPulse(false), 400);
      }
    };
    const handleExternalClose = () => {
      setPeekOpen(false);
      setPeekItem(null);
    };
    const handleExternalTab = ({ tabId }: { tabId: PeekChipTabId }) => {
      setActiveTab(tabId);
    };
    const handleExternalOpenCS = ({ cardId }: { cardId: string }) => {
      // Open in center stage — could emit navigate event or call workspace method
      setPeekOpen(false);
      setPeekItem(null);
    };

    eventBus.on('fc:peek:open', handleExternalOpen);
    eventBus.on('fc:peek:select', handleExternalSelect);
    eventBus.on('fc:peek:close', handleExternalClose);
    eventBus.on('fc:peek:setTab', handleExternalTab);
    eventBus.on('fc:peek:openCenterStage', handleExternalOpenCS);

    return () => {
      eventBus.off('fc:peek:open', handleExternalOpen);
      eventBus.off('fc:peek:select', handleExternalSelect);
      eventBus.off('fc:peek:close', handleExternalClose);
      eventBus.off('fc:peek:setTab', handleExternalTab);
      eventBus.off('fc:peek:openCenterStage', handleExternalOpenCS);
    };
  }, [displayItems]);

  // Emit peek state changes so Studio can sync
  useEffect(() => {
    _setPeekState({ cardId: peekItem?.id ?? null, activeTab, isOpen: peekOpen });
    eventBus.emit('fc:peek:stateChanged', { cardId: peekItem?.id ?? null, activeTab, isOpen: peekOpen });
  }, [peekItem, peekOpen, activeTab]);

  // Persist to storage whenever the database changes.
  // The `database` prop, if provided, is the controlled state from the
  // parent; in that case the parent owns persistence. But the original
  // bug here was that this effect ONLY ran when `database` was
  // undefined, which meant a `database`-prop-driven mount never
  // persisted. Now we always persist when this component is the source
  // of truth (no `database` prop AND we own the state via `db`).
  // When the parent passes `database` AND we have local `db`, we still
  // persist `db` because that's the live state we mutate.
  useEffect(() => {
    /**
     * F 497 — A PAGED DATABASE IS NOT WRITTEN BY THIS SURFACE.
     *
     * This effect saves the database on every change to it — including the
     * change that happens when the surface MOUNTS. For a paged database
     * that is a write of a record the surface did not change, and the
     * record is the graph's: its pages are the items and its descriptor is
     * the only pointer to them.
     */
    if (isPagedDatabase) return;
    /**
     * F 497 — AND A MOUNT IS NOT A CHANGE.
     *
     * The effect's dependency is the `db` STATE, and `db` is set on mount
     * (the lazy initialiser, and the id-change effect). So the effect ran
     * on mount and PERSISTED the record the surface had just read — or, on
     * a cold unhydrated store, the synthetic record it had just
     * manufactured. That write is what replaced the live map of seven
     * records with one.
     *
     * ⇒ ONLY A REAL MUTATION WRITES. `dirtyRef` is set by
     *   `handleDatabaseChange`, which every mutation path goes through.
     */
    if (!dirtyRef.current) return;
    try {
      saveDatabase(db);
    } catch {}
    onDatabaseChange?.(db);
  }, [db, database, onDatabaseChange, isPagedDatabase]);

  // Listen for external 'database:refresh' events (e.g. after a seed populates items
  // in localStorage while this component is already mounted). Without this, the
  // useState lazy initializer would have loaded the empty db at mount time and
  // subsequent saves would overwrite the freshly-seeded items.
  useEffect(() => {
    const handler = (data: any) => {
      if (!data || data.databaseId !== id) return;
      const fresh = loadDatabase(id);
      if (fresh) {
        setDb(fresh);
      }
    };
    eventBus.on('database:refresh', handler);
    return () => eventBus.off('database:refresh', handler);
  }, [id]);

  // View config registry — the single source of truth for "what view am I on
  // and how should items be shaped". Replaces the old ad-hoc viewMode state.
  const {
    views,
    activeView,
    setActiveViewId,
    createView,
    deleteView,
    renameView,
    setGroupBy,
    setBoardOptions,
    setDateGrouping,
    setFilter,
    activeFilter,
    select,
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE GRAPH'S VIEWS ARE PASSED IN, BECAUSE THE
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ GRAPH IS WHERE THE DATA LIVES.** ⇒⇒□ ⇒⇒□
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ MEASURED 2026-11-01: THE GRAPH HELD
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `{ groupBy: "date", groupByField: "prop_…",
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ groupGranularity: "relative" }` WHILE THE
    // ⇒⇒□ □ ⇒⇒□ ⇒⇒□ LOCALSTORAGE COPY HELD `{ groupBy: "status" }` —
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ AND THE SCREEN RENDERED THE LOCALSTORAGE ONE.**
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ WITHOUT THIS, THE GROUPING WAS SAVED
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ BUT NOT SHOWN AFTER A RELOAD.**
  } = useViewConfig(db.id, db.views);

  // F 378 — copy this database's legacy views into the core adapter ONCE, so
  // the Agent Tool Layer can read them at `view:<dbId>`.
  //
  // Fire-and-forget and deliberately NOT awaited: the UI reads the legacy key
  // for its own state either way, so the database view renders identically
  // whether this succeeds, is skipped, or fails. It exists to populate the
  // graph, not to become a dependency of this surface.
  React.useEffect(() => {
    void runViewMigrationOnBoot(db.id);
  }, [db.id]);

  const [globalSearch, setGlobalSearch] = useState('');
  // F 377 — the search field's open/closed state. Separate from `globalSearch`
  // on purpose: the QUERY is transient (never persisted — see the F 377
  // addendum audit), while the field's SHAPE is presentation. Collapsing the
  // field must never discard what the user typed.
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  // F 280 — the property filter is no longer component state.
  //
  // It USED to be the `useState` on the line above this one, commented "applies
  // to every view", which meant: switching views carried your filter along
  // with you, and a reload threw it away. Two tabs could not hold two filters
  // because there was only ever one filter on the surface.
  //
  // It now lives on the view record, beside sortBy/sortDir/groupBy, and arrives
  // through `useViewConfig` exactly as those do — so it is per-view and it is
  // persisted by the same saveViews call. `activeFilter` is `{}` when the
  // active view has no filter, which is byte-identical to the old initial value.
  const propertyFilter = activeFilter;
  const setPropertyFilter = useCallback(
    (next: PropertyFilter | ((prev: PropertyFilter) => PropertyFilter)) => {
      const resolved = typeof next === 'function' ? next(propertyFilter) : next;
      if (activeView) setFilter(activeView.id, resolved);
    },
    [propertyFilter, activeView, setFilter]
  );
  const [isPropertiesOpen, setIsPropertiesOpen] = useState(false);
  const [isNewItemOpen, setIsNewItemOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  // F 473 - the mobile view-manager popover. The view MENU BODY is shared with
  // the desktop through `ViewSwitcherBody`; only the shell differs.
  const [mobileViewsOpen, setMobileViewsOpen] = useState(false);
  // F 473 — the mobile create-view adapter. It keeps the historical public
  // name so the F 293 source-text contract (TWO create-callback mount sites in
  // this file: the desktop switcher and the mobile one) still holds.
  const mobileCreateView = (type: ViewLayout) => { createView(type); setMobileViewsOpen(false); };
  const [newItemTitle, setNewItemTitle] = useState('');
  // Kanban grouping is now per-view (activeView.groupBy), not global.

  // Handle database changes
  const handleDatabaseChange = useCallback((updatedDb: Database) => {
    // F 497 — THIS IS A MUTATION. Mark the record dirty so the save effect
    // writes it. Every real edit funnels through here (add / update /
    // delete item, view edits), and nothing else marks it — so a mount,
    // an id change, or a `database:refresh` reload never writes.
    dirtyRef.current = true;
    setDb(updatedDb);
    onDatabaseChange?.(updatedDb);
  }, [onDatabaseChange]);

/**
 * F 506 — THE VIEW'S TYPE, FROM ITS ID.
 *
 * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE UI MINTS `view_<dbId>_<type>` AND THE
 * GRAPH HOLDS `<dbId>_<type>` — TWO DIFFERENT STRINGS FOR THE SAME VIEW.**
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE TYPE IS THE LAST SEGMENT OF BOTH, AND
 * IT IS THE SEMANTIC KEY: "WHICH VIEW IS THIS — THE BOARD, THE LIST,
 * THE GALLERY".** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
 *
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ AND THE GRAPH's OWN ID IS NOT
 * REWRITTEN, BECAUSE OTHER RECORDS POINT AT IT, AND REWRITING IT WOULD
 * BREAK EVERY POINTER.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
 */
function viewTypeFor(viewId: string, views: DatabaseViewConfig[]): string | undefined {
  const fromId = viewId.split('_').pop();
  if (fromId && views.some((v) => (v.type ?? v.layout) === fromId)) return fromId;
  return undefined;
}

  /**
   * F 506 — THE VIEW-CHANGE PERSISTENCE BRIDGE.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE UI's `useViewConfig` PERSISTS TO
   * `localStorage`. THAT IS NOT A BUG — IT IS THE PRE-F 135 LAYER, AND
   * IT IS HOW THE UI READS ITS OWN STATE.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
   * **⇒⇒□ BUT THE GRAPH IS WHERE THE DATA LIVES, AND A VIEW THAT ONLY
   * REACHES `localStorage` DOES NOT SURVIVE A DIFFERENT BROWSER, A
   * DIFFERENT DEVICE, OR THE SERVER READING THE VIEW.** ⇒⇒□ ⇒⇒□ ⇒⇒□
   * ⇒⇒□ ⇒⇒□
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE PATH TO THE GRAPH, MEASURED, NOT
   * ASSUMED: ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
   *   `useViewConfig` → this bridge → `db.views` → `handleDatabaseChange`
   *   → the save effect → `saveDatabase(db)` → `RemoteState` → the graph.
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ `saveDatabase` IS THE ONLY WRITER THAT
   * REACHES THE GRAPH, AND IT IS REACHED ONLY WHEN `dirtyRef` IS TRUE —
   * WHICH IS WHY THIS BRIDGE CALLS `handleDatabaseChange` AND NOT
   * `setDb`.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ A `setDb` WOULD UPDATE THE
   * UI AND THE SAVE EFFECT WOULD REFUSE TO WRITE.**
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ AND THE MERGE IS NOT A REPLACE. ⇒⇒□ ⇒⇒□
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ `db.views` IS THE DATABASE'S OWN LIST OF VIEWS,
   * AND THE UI's `views` COME FROM `useViewConfig` — WHICH IS A DIFFERENT
   * LIST WITH A DIFFERENT ID SCHEME.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
   * **⇒⇒□ REPLACING `db.views` WITH THE UI's LIST WOULD REWRITE THE
   * DATABASE'S VIEW IDS, AND EVERY VIEW THAT POINTS AT ONE WOULD BREAK.**
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ SO THE BRIDGE MERGES BY VIEW ID: THE
   * DATABASE'S LIST KEEPS ITS SHAPE, AND ONLY THE CHANGED FIELDS ARE
   * COPIED ACROSS.**
   */
  const persistViewChange = useCallback((
    viewId: string,
    patch: Partial<DatabaseViewConfig>,
  ) => {
    // ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ MEASURED AGAINST LIVE PRODUCTION,
    // 2026-11-01: THE GRAPH'S VIEW IDS AND THE UI's VIEW IDS ARE
    // DIFFERENT STRINGS FOR THE SAME VIEW.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
    // **⇒⇒□ THE GRAPH HOLDS `page-1793531738055-lqiks0hc7_kanban`; THE UI
    // MINTS `view_page-1793531738055-lqiks0hc7_kanban`.** ⇒⇒□ ⇒⇒□ ⇒⇒□
    // ⇒⇒□ ⇒⇒□ **⇒⇒□ AND THE FIRST DRAFT OF THIS BRIDGE MATCHED BY
    // `id` — SO IT MATCHED NOTHING, PATCHED NOTHING, AND THE TOGGLES
    // WERE NEVER SAVED.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE GRAPH WAS
    // READ BACK WITH `view:<dbId>` EMPTY AND THE DATABASE RECORD'S
    // `views` CARRYING THE OLD TWO-VIEW LIST.**
    //
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ SO THE MATCH IS BY `type` — THE
    // SEMANTIC KEY ("WHICH VIEW IS THIS: THE BOARD, THE LIST") — AND
    // THE GRAPH's OWN ID IS KEPT.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE
    // ID IS NOT REWRITTEN, BECAUSE OTHER RECORDS POINT AT IT.** ⇒⇒□
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
    // ⇒⇒⇒⇒⇒⇒⇒⇒ F 509 — THE STALE-CLOSURE LOSS, MEASURED AGAINST THE
    // ⇒⇒⇒⇒⇒⇒⇒⇒ LIVE GRAPH ON 2026-11-01. ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□
    //
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE SYMPTOM: THE GRAPH HELD `groupBy: "date"`
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ AND NOTHING ELSE — `groupByField` AND
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `groupGranularity` WERE ABSENT.** ⇒⇒□ ⇒⇒□ ⇒⇒□
    //
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE CAUSE: `ViewSwitcher`'s DATE-PROPERTY ROW
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ CALLS `onChangeDateGrouping` AND THEN
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `onChangeGroupBy` — TWO CALLS IN ONE TICK.** ⇒⇒□
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ BOTH ROUTE INTO THIS FUNCTION, AND BOTH READ
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `db` FROM THE RENDER'S CLOSURE — WHICH IS THE
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ *PRE-UPDATE* VALUE, BECAUSE REACT'S `setDb` IS
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ASYNCHRONOUS.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ SO THE
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ SECOND CALL COMPUTED ITS RESULT FROM THE OLD
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ STATE AND **DROPPED THE FIRST CALL'S FIELDS** —
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE SECOND PATCH WAS `{ groupBy: 'date' }` AND THE
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ FIRST PATCH'S `groupByField` WAS GONE.**
    //
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE FIX IS THE FUNCTIONAL `setDb` FORM: PATCH
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ AGAINST THE *LATEST* STATE, NOT A CAPTURED ONE.**
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE MERGE MUST HAPPEN INSIDE THE UPDATER.
    setDb((prev) => patchViewInPlace(prev, viewId, patch as Record<string, unknown>, viewTypeFor));
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ AND THE DIRTY FLAG MUST BE SET HERE** — the
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ updater above does NOT call `handleDatabaseChange`,
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ and the save effect is gated on `dirtyRef`, so a
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ view change that skipped the flag would NEVER BE
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ WRITTEN TO THE GRAPH.
    dirtyRef.current = true;
  }, [handleDatabaseChange]);

  /**
   * F 506 — THE GROUP-CHANGE HANDLER.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ IT DOES BOTH THINGS, AND IT MUST.**
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ `setGroupBy` UPDATES THE UI's OWN
   * LIST — WHICH IS WHAT THE UI RENDERS — AND `persistViewChange` PUTS
   * THE SAME VALUE IN `db.views`, WHICH IS WHAT THE GRAPH HOLDS.** ⇒⇒□
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ DOING ONLY THE FIRST LEAVES THE APP
   * PRETTY AND THE DATA LOST; DOING ONLY THE SECOND LEAVES THE APP
   * SHOWING THE OLD BOARD.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
   */
  const handleChangeGroupBy = useCallback((id: string, mode: GroupByMode) => {
    setGroupBy(id, mode);
    persistViewChange(id, { groupBy: mode });
  }, [setGroupBy, persistViewChange]);

  /**
   * F 506 — THE BOARD-OPTION HANDLER.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ ONE HANDLER FOR ALL THREE TOGGLES,
   * AND IT PATCHES ONLY THE KEY THAT CHANGED.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
   * **⇒⇒□ A HANDLER THAT WROTE ALL THREE VALUES EVERY TIME WOULD RESET
   * THE OTHER TWO TO WHATEVER THE UI THOUGHT THEY WERE** — and the UI's
   * copy is the one that is stale when the graph is the truth. ⇒⇒□ ⇒⇒□
   * ⇒⇒□ ⇒⇒□ ⇒⇒□
   */
  const handleChangeBoardOptions = useCallback((
    id: string,
    patch: { wrapContent?: boolean; colorColumns?: boolean; showIcons?: boolean },
  ) => {
    // ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ IT MUST DO BOTH, AND THE ORDER
    // MATTERS.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ `setBoardOptions` UPDATES
    // THE UI's OWN LIST — WHICH IS WHAT `activeView` AND THE BOARD READ,
    // SO THE SCREEN CHANGES NOW.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□
    // `persistViewChange` PUTS THE SAME VALUE IN `db.views` — WHICH IS
    // WHAT THE GRAPH HOLDS, SO IT SURVIVES A RELOAD.** ⇒⇒□ ⇒⇒□ ⇒⇒□
    // ⇒⇒□ ⇒⇒□ **⇒⇒□ DOING ONLY THE FIRST LEAVES A TOGGLE THAT LOOKS
    // SET AND IS NOT SAVED. DOING ONLY THE SECOND LEAVES A TOGGLE THAT
    // IS SAVED AND DOES NOTHING.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ AND `setBoardOptions` COMES FIRST,
    // BECAUSE IT IS THE ONE THAT MAKES THE TOGGLE VISIBLE — AND A
    // TOGGLE THE OPERATOR CANNOT SEE CHANGE IS NOT FEEDBACK.**
    setBoardOptions(id, patch);
    persistViewChange(id, patch);
  }, [setBoardOptions, persistViewChange]);

  /**
   * F 509 — THE DATE-GROUPING HANDLER.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ IT DOES BOTH, FOR THE SAME REASON
   * `handleChangeBoardOptions` DOES.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□
   * `setDateGrouping` UPDATES THE UI's OWN LIST — WHICH IS WHAT `activeView`
   * AND THE BOARD READ, SO THE SCREEN CHANGES NOW. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
   * **⇒⇒□ `persistViewChange` PUTS THE SAME VALUE IN `db.views` — WHICH IS
   * WHAT THE GRAPH HOLDS, SO IT SURVIVES A RELOAD.**
   */
  const handleChangeDateGrouping = useCallback((
    id: string,
    patch: {
      groupByField?: string;
      groupGranularity?: import('../../../core/views/groupRecords').GroupGranularity;
    },
  ) => {
    setDateGrouping(id, patch);
    persistViewChange(id, patch);
  }, [setDateGrouping, persistViewChange]);


  // When a new card is selected, reset tab to Content and trigger pulse
  useEffect(() => {
    if (peekItem) {
      setActiveTab('content'); // always reset to Content on new card
      setPeekPulse(true);
      const t = setTimeout(() => setPeekPulse(false), 400);
      return () => clearTimeout(t);
    }
  }, [peekItem?.id]);

  const handleItemSelect = useCallback((item: DatabaseItem) => {
    setPeekItem(item);
    setPeekOpen(true);
    eventBus.emit('fc:peek:cardSelected', { cardId: item.id });
  }, []);

  const handlePeekClose = useCallback(() => {
    setPeekOpen(false);
    setPeekItem(null);
    eventBus.emit('fc:peek:panelClosed', {});
  }, []);

  // Filter items — text search + property filter (universal across all views)
  const filteredItems = useMemo(() => {
    let items = displayItems;
    // Text search
    if (globalSearch) {
      const searchLower = globalSearch.toLowerCase();
      items = items.filter(item =>
        item.title.toLowerCase().includes(searchLower) ||
        item.properties.some(prop =>
          String(prop.value).toLowerCase().includes(searchLower)
        )
      );
    }
    // Property filter (universal: applies to every view, not just Gallery).
    // We pass a permissive quick/status filter here (all/all) so only the
    // property filter narrows the result. The full applyFilters signature
    // requires timeField/quick/status which the calendar-specific flow owns.
    items = items.filter(item => {
      if (!propertyFilter || Object.keys(propertyFilter).length === 0) return true;
      for (const [propId, selectedIds] of Object.entries(propertyFilter)) {
        if (!selectedIds || selectedIds.length === 0) continue;
        const propVal = item.properties.find(p => p.propertyId === propId)?.value;
        if (propVal == null) return false;
        // Normalize the value to an array of strings for comparison
        const valueIds = Array.isArray(propVal)
          ? propVal.map(String)
          : (typeof propVal === 'object' && propVal !== null)
            ? [String((propVal as any).id ?? '')]
            : [String(propVal)];
        const intersects = selectedIds.some(id => valueIds.includes(id));
        if (!intersects) return false;
      }
      return true;
    });
    return items;
  }, [displayItems, db.schema.properties, globalSearch, propertyFilter]);

  // Discover all properties + their values (for the property filter popover)
  const allProperties = useMemo(
    () => discoverProperties(db.schema.properties, displayItems),
    [displayItems, db.schema.properties]
  );
  const activePropertyFilterCount = Object.values(propertyFilter).reduce(
    (acc, opts) => acc + (opts.length > 0 ? 1 : 0), 0
  );

  // F 375 — the popover BODY, lifted out of the toolbar's funnel icon so the
  // `+ Filter` pill in the new FilterRow can render it. The markup below is the
  // same markup that used to sit inside the toolbar's <Popover.Dropdown>: same
  // properties, same `UniversalPropertyFilterRow`, same delete-on-empty write to
  // `setPropertyFilter`. Only the trigger moved; the filter logic did not change.
  const filterPopoverBody = (
    <>
      {allProperties.length === 0 ? (
        <Text size="xs" c="dimmed">No filterable properties yet. Add a status, tag, or select property.</Text>
      ) : (
        allProperties.map(prop => (
          <UniversalPropertyFilterRow
            key={prop.id}
            property={prop}
            selected={propertyFilter[prop.id] || []}
            onChange={(values) => {
              setPropertyFilter(prev => {
                const next = { ...prev };
                if (values.length === 0) delete next[prop.id];
                else next[prop.id] = values;
                return next;
              });
            }}
          />
        ))
      )}
    </>
  );

  // Shape filtered items for the active view (sort + group). This is what
  // every view renders — single source of truth, no per-view sorting.
  const { items: viewItems } = useMemo(
    () => select(filteredItems),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredItems, activeView]
  );

  /**
   * F 497 — THE NUMBER THE TOOLBAR SHOWS, IN BOTH TOOLBARS.
   *
   * MEASURED, and this is why the label is computed ONCE and used twice: the
   * phone toolbar and the desktop toolbar are two separate branches, and the
   * count that was patched into the desktop one (a `Text` with
   * `data-fc-item-count`) does not exist on the phone at all — the phone
   * renders a `Badge` with `viewItems.length`, which for a paged database is
   * the PAGE (60), not the database (16,783). So a phone user would have seen
   * "60" and a desktop user "16,783" for the same database, which is exactly
   * the disagreement this dispatch is about.
   *
   * ⇒ ONE LABEL, TWO PLACES. The page is what was READ; the total is what
   *   the database OWNS.
   */
  const itemCountLabel = pagedLoading
    ? 'Loading…'
    : pagedTotal !== null
      ? pagedTotal.toLocaleString('en-US')
      : String(viewItems.length);

  // F 141 C — ONE resolved layout for the whole surface.
  //
  // Every render branch below used to read `activeView.type` directly, which
  // is the PRE-F 135 key. F 135/F 138 made `layout` canonical with `type` as a
  // read fallback, so a view carrying `layout: 'card'` AND a legacy
  // `type: 'list'` would have satisfied BOTH the legacy branch and the new one
  // and rendered two layouts stacked. Resolving once, here, removes that class
  // of bug instead of papering over it.
  //
  // `activeView` is typed as the LEGACY DatabaseViewConfig, which has no
  // `layout` key. Rather than an `as any` cast, run it through F 135's
  // sanctioned adapter — the function built for precisely this shape gap. It
  // returns a real ViewConfig (layout canonical, `type` read as fallback).
  const fcrViewConfig: ViewConfig =
    coerceViewConfig(activeView, db.id) ??
    ({ layout: 'list', filter: {}, sort: [], visiblePropertyIds: [], propertyOrder: [] } as ViewConfig);

  const fcrLayout: string = fcrViewConfig.layout;

  /** DatabaseItem[] -> the shape F 136's generic renderers take. */
  const fcrRows: ViewRendererRow[] = useMemo(
    () =>
      viewItems.map((i: any) => ({
        id: i.id,
        title: i.title,
        properties: i.properties,
      })) as ViewRendererRow[],
    [viewItems]
  );

  // Handle add item
  /**
   * F 509 - THE ITEM THE VIEW ALREADY BUILT, WHEN IT BUILT ONE.
   *
   * MEASURED: `KanbanView` builds a COMPLETE item and calls
   * `onItemCreate(newItem)` - the item already has its title, its column
   * property and its timestamps. MEASURED: this handler took NO argument
   * and read `newItemTitle` from the SURFACE's own "New item" modal state,
   * which the board path never sets. So `newItemTitle` was `''`,
   * `if (!newItemTitle.trim()) return;` FIRED, AND THE BOARD'S "Add item"
   * DID NOTHING.
   *
   * MEASURED WHEN IT BROKE: `git log -S` puts BOTH the `onItemCreate?.(
   * newItem)` call AND this guard in the SAME commit - 5680194, the
   * initial extraction - so the board's "Add item" HAS NEVER WORKED. It is
   * NOT a regression from F 509; it is a pre-existing defect that blocked
   * F 509's proof of the date columns.
   *
   * THE FIX: the handler takes the item the view built, and falls back to
   * building one from the surface's own modal when it has none.
   */
  const handleAddItem = (prebuilt?: DatabaseItem) => {
    // F 497 - A PAGED DATABASE IS NOT WRITTEN FROM HERE. See the notice.
    if (isPagedDatabase) return;

    // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE VIEW'S OWN ITEM WINS.** ⇒⇒□ ⇒⇒□ ⇒⇒□
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ IT CARRIES THE COLUMN THE CARD WAS ADDED TO, AND
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE SURFACE'S MODAL HAS NO SUCH INFORMATION.**
    if (prebuilt) {
      const withDefaults = { ...prebuilt, databaseId: db.id };
      const updatedDb = {
        ...db,
        items: [...db.items, withDefaults],
        updatedAt: new Date(),
      };
      handleDatabaseChange(updatedDb);
      return;
    }

    if (!newItemTitle.trim()) return;

    const newItem = createDatabaseItem(
      `item_${Date.now()}`,
      db.id,
      newItemTitle
    );

    // New items get a default status property so the kanban columns
    // immediately have a home for them.
    if (activeView.groupBy === 'status' || activeView.groupBy === undefined) {
      newItem.properties.push({
        propertyId: 'status',
        value: { id: 'status_backlog', name: 'Backlog', color: '#94a3b8', state: 'active' },
      });
    }

    const updatedDb = {
      ...db,
      items: [...db.items, newItem],
      updatedAt: new Date(),
    };

    handleDatabaseChange(updatedDb);
    setIsNewItemOpen(false);
    setNewItemTitle('');
  };

  /**
   * F 509 - A CARD ADDED TO A DATE COLUMN.
   *
   * MEASURED: `GroupedBoardView` HAD NO "Add item" CONTROL AT ALL, so the
   * DATE-MODE board could not receive a card while the status board could.
   * The date board is a first-class board.
   *
   * THE COLUMN'S DATE BECOMES THE CARD'S DATE, ON THE PROPERTY THE BOARD
   * IS GROUPED BY - so the card lands in the column it was added to, and
   * the grouping stays TRUE.
   *
   * A COLUMN WITH NO DATE (a relative window, or a property-shaped column)
   * GETS NO DATE, because inventing one would be a lie.
   */
  const handleCreateItemInColumn = useCallback(
    (column: { id: string; date?: string }) => {
      if (isPagedDatabase) return;
      const title = `Item ${new Date().toLocaleTimeString()}`;
      const newItem = createDatabaseItem(`item_${Date.now()}`, db.id, title);
      if (column.date && activeView?.groupByField) {
        newItem.properties.push({
          propertyId: activeView.groupByField,
          value: column.date,
        });
      }
      handleDatabaseChange({
        ...db,
        items: [...db.items, newItem],
        updatedAt: new Date(),
      });
    },
    [activeView, db, handleDatabaseChange, isPagedDatabase],
  );

  // Handle update item
  const handleUpdateItem = (item: DatabaseItem) => {
    // F 497 — A PAGED DATABASE IS NOT WRITTEN FROM HERE. See the notice.
    if (isPagedDatabase) return;
    const updatedDb = {
      ...db,
      items: db.items.map(i => (i.id === item.id ? item : i)),
      updatedAt: new Date(),
    };
    handleDatabaseChange(updatedDb);
    // Update peek item if it's the same item
    if (peekItem?.id === item.id) {
      setPeekItem(item);
    }
  };

  // Handle delete item
  const handleDeleteItem = (itemId: string) => {
    // F 497 — A PAGED DATABASE IS NOT WRITTEN FROM HERE. See the notice.
    if (isPagedDatabase) return;
    const updatedDb = {
      ...db,
      items: db.items.filter(i => i.id !== itemId),
      updatedAt: new Date(),
    };
    handleDatabaseChange(updatedDb);
    // Clear peek if it's the deleted item
    if (peekItem?.id === itemId) {
      setPeekItem(null);
      setPeekOpen(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // F 371 — the peek's top-bar actions.
  //
  // MEASURED, not assumed: the peek's header carried exactly ONE button
  // (the close ×). Notion carries five: Expand, Share, Copy link, Star and
  // a ⋯ menu. These are the real actions behind them.
  //
  // STARRED — stored on the card's own `properties` channel under the id
  // 'starred', the same way the cover is stored (see setItemCover). That
  // means NO new entity field, NO schema change and NO new storage module:
  // the property channel is already persisted with the database. The
  // resolver reads it back in the same order resolveCover reads 'cover'.
  // ═══════════════════════════════════════════════════════════════════════
  const resolveStarred = useCallback((item: DatabaseItem | null): boolean => {
    if (!item) return false;
    const prop = item.properties.find(p => p.propertyId === 'starred');
    return prop?.value === true || prop?.value === 'true';
  }, []);

  const setItemStarred = useCallback((itemId: string, starred: boolean) => {
    const updatedDb = {
      ...db,
      items: db.items.map(i => {
        if (i.id !== itemId) return i;
        const rest = i.properties.filter(p => p.propertyId !== 'starred');
        const properties = starred
          ? [...rest, { propertyId: 'starred', value: true } as PropertyValue]
          : rest;
        return { ...i, properties, updatedAt: new Date() };
      }),
      updatedAt: new Date(),
    };
    handleDatabaseChange(updatedDb);
    const updatedItem = updatedDb.items.find(i => i.id === itemId);
    if (updatedItem) setPeekItem(updatedItem);
  }, [db, handleDatabaseChange]);

  // The card's shareable URL. The OLD chip copied `freshcards://card/<id>`,
  // which is an app-internal scheme — not openable by anyone else and not
  // pasteable into a chat. The card's page id IS its item id (the Card=Page
  // model), so the page route is the real link.
  const peekShareUrl = useMemo(() => {
    if (!peekItem) return '';
    const { origin } = window.location;
    return `${origin}/page/${encodeURIComponent(peekItem.id)}`;
  }, [peekItem]);

  // Expand / Full Page — open the peeked card's page in the main surface.
  // The card is a page (Card=Page model), so this is a page navigation.
  const handlePeekExpand = useCallback(() => {
    if (!peekItem) return;
    handlePeekClose();
    eventBus.emit('fc:peek:openCenterStage', { cardId: peekItem.id });
    // The database surface owns the router; ask the workspace to select the
    // page, which the route-sync hook turns into a /page/<id> URL.
    try {
      const url = `/page/${encodeURIComponent(peekItem.id)}`;
      window.history.pushState(null, '', url);
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch {
      // If navigation is unavailable the peek still closes; nothing else
      // in the peek depends on it.
    }
  }, [peekItem, handlePeekClose]);

  // Handle duplicate item
  const handleDuplicateItem = (item: DatabaseItem) => {
    const newItem: DatabaseItem = {
      ...item,
      id: `item_${Date.now()}`,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const updatedDb = {
      ...db,
      items: [...db.items, newItem],
      updatedAt: new Date(),
    };
    handleDatabaseChange(updatedDb);
  };

  // Handle property change
  const handlePropertyChange = (itemId: string, propertyId: string, value: any) => {
    const updatedDb = {
      ...db,
      items: db.items.map(i => {
        if (i.id !== itemId) return i;
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE PROPERTY IS *CREATED* WHEN IT IS
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ABSENT.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ MEASURED: THE
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ OLD FORM USED `properties.map(...)`, WHICH CAN
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ONLY *UPDATE* AN EXISTING ENTRY — so setting a
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ VALUE FOR A PROPERTY THE RECORD DOES NOT YET
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ HAVE CHANGED NOTHING, AND THE VALUE WAS
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ SILENTLY DROPPED.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THAT
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ IS WHY A USER-ADDED DATE PROPERTY COULD NOT BE
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ SET ON A CARD: THE LIVE GRAPH SHOWED THE CARDS
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ CARRYING ONLY `status`.**
        const has = i.properties.some(p => p.propertyId === propertyId);
        const properties = has
          ? i.properties.map(p =>
              p.propertyId === propertyId ? { ...p, value } : p,
            )
          : [...i.properties, { propertyId, value }];
        return {
          ...i,
          properties,
          updatedAt: new Date(),
        };
      }),
      updatedAt: new Date(),
    };
    handleDatabaseChange(updatedDb);
    // Update peek item if it's the same item
    if (peekItem?.id === itemId) {
      const updatedItem = updatedDb.items.find(i => i.id === itemId);
      if (updatedItem) {
        setPeekItem(updatedItem);
      }
    }
  };

  // Handle properties change
  const handlePropertiesChange = (properties: any[]) => {
    const updatedDb = {
      ...db,
      schema: {
        ...db.schema,
        properties,
      },
      updatedAt: new Date(),
    };
    handleDatabaseChange(updatedDb);
  };

  // Handle export
  const handleExport = () => {
    const dataStr = JSON.stringify(db, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `database_${db.id}_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Peek Chip (Back of Card) — 3-tab chip navigation
  const peekChipTabBar = (
    <Box style={{ padding: '12px 16px 0', borderBottom: '1px solid var(--fc-border-secondary)' }}>
      <ChipRow>
        <Chip
          label="Content"
          state={activeTab === 'content' ? 'active' : 'inactive'}
          onClick={() => setActiveTab('content')}
        />
        <Chip
          label="Properties"
          state={activeTab === 'properties' ? 'active' : 'inactive'}
          onClick={() => setActiveTab('properties')}
        />
        <Chip
          label="Details"
          state={activeTab === 'details' ? 'active' : 'inactive'}
          onClick={() => setActiveTab('details')}
        />
      </ChipRow>
    </Box>
  );

  // F 200 Bug C — persist a card cover into the 'cover' property channel that
  // resolveCover() reads first. null removes it, which falls the card back to
  // its deterministic id-hash gradient.
  const setItemCover = useCallback((itemId: string, gradient: string | null) => {
    const updatedDb = {
      ...db,
      items: db.items.map(i => {
        if (i.id !== itemId) return i;
        const rest = i.properties.filter(p => p.propertyId !== 'cover');
        const properties = gradient
          ? [...rest, { propertyId: 'cover', value: gradient } as PropertyValue]
          : rest;
        return { ...i, properties, updatedAt: new Date() };
      }),
      updatedAt: new Date(),
    };
    handleDatabaseChange(updatedDb);
    const updatedItem = updatedDb.items.find(i => i.id === itemId);
    if (updatedItem) setPeekItem(updatedItem);
  }, [db, handleDatabaseChange]);

  // F 200 Bug C — card cover, shown and editable inside the opened card.
  //
  // The gradient on the card face was never stored: resolveCover() falls back
  // to a deterministic hash of the item id, so the face and the panel were
  // reading the same function but only the face called it. That is why the
  // thumbnail had a cover and the opened panel had none, and why there was
  // nothing to edit.
  //
  // Storing it uses the channel resolveCover() already checks FIRST
  // (item.properties['cover']), so no new entity field is needed and the
  // existing resolver keeps working unchanged.
  // F 201 D — the cover STRIP stays in the panel; the permanent 12-swatch grid
  // and the inline "Remove cover" button are gone (D.1). The picker now opens
  // on demand from a "Change cover" affordance that appears on hover or
  // keyboard focus of the strip (D.2), so an opened card shows only its
  // cover and its content.
  //
  // D.4: still persists through setItemCover into properties['cover'] via the
  // existing resolver. No new storage channel.
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);
  // D.2 — the affordance is shown on hover OR keyboard focus. Implemented as
  // React state rather than a `:hover` CSS rule because the resting styles are
  // INLINE, and an inline declaration outranks any stylesheet selector: a
  // `[data-fc-cover-strip]:hover [data-fc-cover-change]` rule was measured at
  // opacity 0 while `:hover` matched. State cannot lose that race, and it is
  // reachable from a test.
  const [coverHot, setCoverHot] = useState(false);

  const coverStrip = peekItem ? (
    <Box style={{ padding: '12px 16px 0' }}>
      <div
        data-fc-cover-strip
        tabIndex={0}
        role="button"
        aria-label="Change cover"
        onMouseEnter={() => setCoverHot(true)}
        onMouseLeave={() => setCoverHot(false)}
        onFocus={() => setCoverHot(true)}
        onBlur={() => setCoverHot(false)}
        onClick={() => setCoverPickerOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setCoverPickerOpen(true);
          }
        }}
        style={{
          height: 72, borderRadius: 8, position: 'relative', cursor: 'pointer',
          background: resolveCover(peekItem, db),
          backgroundSize: 'cover', backgroundPosition: 'center',
        }}
      >
        <span
          data-fc-cover-change
          style={{
            position: 'absolute', right: 8, top: 8,
            padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 500,
            background: 'rgba(0,0,0,0.55)', color: '#fff',
            opacity: coverHot ? 1 : 0,
            visibility: coverHot ? 'visible' : 'hidden',
            transition: 'opacity 120ms ease',
          }}
        >
          Change cover
        </span>
      </div>
      {coverPickerOpen && (
        <CoverPickerModal
          opened={coverPickerOpen}
          onClose={() => setCoverPickerOpen(false)}
          current={resolveCover(peekItem, db)}
          onPick={(src) => {
            if (src === null) setItemCover(peekItem.id, null);
            else if (src.kind === 'gradient' || src.kind === 'colour') {
              // Gradients and solid colours are both CSS background values
              // and both go down the same channel.
              setItemCover(peekItem.id, src.value);
            } else if (src.kind === 'url') {
              setItemCover(peekItem.id, `url("${src.value}")`);
            } else {
              setItemCover(peekItem.id, `url("${src.dataUrl}")`);
            }
          }}
        />
      )}
    </Box>
  ) : null;

  // Tab 1: Content — PageEditor (Card=Page model: DatabaseItem.id = Page.id)
  const contentTab = (
    <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {peekItem ? (
        // F 202 E — document card. Scoped to the peek panel only; the
        // full-page editor is untouched.
        <div className="fc-peek-doc">
          <PageEditor
            pageId={peekItem.id}
            peekMode={true}
            className="peek-content-editor"
          />
        </div>
      ) : (
        <Box style={{ padding: 16, textAlign: 'center', color: 'var(--fc-text-muted)' }}>
          <Text size="sm" c="dimmed">No card selected</Text>
        </Box>
      )}
      {/* Support-act chips */}
      <Box style={{ padding: '8px 16px', borderTop: '1px solid var(--fc-border-secondary)' }}>
        <ChipRow>
          <Chip label="Copy link" variant="tag" onClick={() => { navigator.clipboard.writeText(`freshcards://card/${peekItem?.id}`); }} />
        </ChipRow>
      </Box>
    </Box>
  );

  // Tab 2: Properties — PropertyChip row
  const propertiesTab = (
    <Box style={{ padding: '16px' }}>
      {peekItem && db.schema.properties.length === 0 && (
        <Text size="sm" c="dimmed" mb="md">No properties yet. Add them in the database schema.</Text>
      )}
      {peekItem && db.schema.properties.length > 0 && (
        <>
          <Stack gap="xs">
            {db.schema.properties.map(propDef => {
              // F 200 Bug A — the title column is special. `item.title` is the
              // real field; there is no `properties[]` entry with
              // propertyId 'title', so a plain .find() always misses it and the
              // chip renders "No Title" for a card whose face says "test".
              // ListView.tsx:94 already special-cased this; the peek panel did not.
              const isTitleProp = propDef.id === 'title';
              const propVal = peekItem.properties.find(p => p.propertyId === propDef.id);
              const displayValue = isTitleProp ? peekItem.title : propVal?.value;
              return (
                <Box key={propDef.id}>
                  <Text size="xs" c="dimmed" mb={4}>{propDef.name}</Text>
                  <PropertyChip
                    property={propDef}
                    value={displayValue}
                    /* F 490 — the owner of this date, so a reminder can
                       point at the record that holds it. */
                    ownerId={peekItem.id}
                    ownerPropertyId={propDef.id}
                    notificationText={peekItem.title}
                    onChange={(newVal) => {
                      if (isTitleProp) {
                        handleUpdateItem({ ...peekItem, title: String(newVal ?? '') });
                        return;
                      }
                      handlePropertyChange(peekItem.id, propDef.id, newVal);
                    }}
                  />
                </Box>
              );
            })}
          </Stack>
          {/* Support-act chips */}
          <ChipRow style={{ marginTop: 12 }}>
            <Chip label="+ Add property" variant="add" disabled />
            <Chip label="Manage schema" variant="tag" disabled />
          </ChipRow>
        </>
      )}
    </Box>
  );

  // Tab 3: Details — metadata display
  const detailsTab = (
    <Box style={{ padding: '16px' }}>
      <Stack gap="xs">
        <Group justify="space-between">
          <Text size="xs" c="dimmed">Created</Text>
          <Text size="sm">{peekItem?.createdAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</Text>
        </Group>
        <Group justify="space-between">
          <Text size="xs" c="dimmed">Updated</Text>
          <Text size="sm">{peekItem?.updatedAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</Text>
        </Group>
        <Group justify="space-between">
          <Text size="xs" c="dimmed">Database</Text>
          <Text size="sm">{db.name}</Text>
        </Group>
        <Group justify="space-between">
          <Text size="xs" c="dimmed">Card ID</Text>
          <Text size="xs" style={{ fontFamily: 'var(--fc-font-mono)' }}>{peekItem?.id}</Text>
        </Group>
      </Stack>
      {/* Tags section */}
      <Box mt="md">
        <Text size="xs" c="dimmed" mb="xs" tt="uppercase" fw={500}>Tags</Text>
        <ChipRow>
          {(peekItem?.tags || []).length === 0 && (
            <Text size="xs" c="dimmed">No tags</Text>
          )}
          {(peekItem?.tags || []).map(tag => (
            <Chip key={tag} label={tag} variant="tag" />
          ))}
        </ChipRow>
      </Box>
      {/* Debug JSON */}
      <Box mt="md">
        <Text
          size="xs"
          c="dimmed"
          tt="uppercase"
          fw={500}
          mb="xs"
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setShowDebug(o => !o)}
        >
          Debug {showDebug ? '▲' : '▼'}
        </Text>
        {showDebug && (
          <Paper p="sm" radius="md" withBorder style={{ background: 'var(--fc-bg-secondary)', maxHeight: 200, overflow: 'auto' }}>
            <Text size="xs" style={{ fontFamily: 'var(--fc-font-mono)', whiteSpace: 'pre' }}>
              {JSON.stringify(peekItem, null, 2)}
            </Text>
          </Paper>
        )}
      </Box>
      {/* Support-act chips */}
      <ChipRow style={{ marginTop: 12 }}>
        <Chip label="Copy Card ID" variant="tag" onClick={() => navigator.clipboard.writeText(peekItem?.id || '')} />
        <Chip label="Copy link" variant="tag" onClick={() => navigator.clipboard.writeText(`freshcards://card/${peekItem?.id}`)} />
      </ChipRow>
    </Box>
  );

  // New peek content with 3-tab structure + pulse animation
  const peekContent = useMemo(() => {
    if (!peekItem) return null;
    return (
      <Box
        className={`peek-chip-content ${peekPulse ? 'pulse' : ''}`}
        style={{
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          animation: peekPulse ? 'fc-peek-pulse 0.4s ease-out' : 'none',
        }}
      >
        {/* F 200 Bug C — cover strip sits above the tabs so it is visible on
            every tab, not just Content. */}
        {activeTab === 'content' && coverStrip}
        {/* ChipNav tab bar */}
        {peekChipTabBar}
        {/* Tab content */}
        <Box style={{ flex: 1, overflowY: 'auto' }}>
          {activeTab === 'content' && contentTab}
          {activeTab === 'properties' && propertiesTab}
          {activeTab === 'details' && detailsTab}
        </Box>
      </Box>
    );
  }, [peekItem, activeTab, peekPulse, showDebug, db, coverStrip]);

  return (
    <BaseSurface
      id={id}
      phase={phase}
      title={title}
      description={description}
      isActive={isActive}
      isLoading={isLoading}
      error={error || ''}
      className={`database-view-surface-wrapper ${className}`}
      cover={
        onCoverChange ? (
          <PageCover
            cover={pageCover}
            coverLayout={pageCoverLayout as CoverLayout}
            coverPosition={pageCoverPosition}
            canEdit={true}
            onChange={onCoverChange}
          />
        ) : undefined
      }
    >
      <div className="database-view-content" style={{ height: '100%', position: 'relative' }}>
        {/* Peek panel — bottom sheet on mobile, side panel on desktop */}
        {isMobile ? (
          <PeekBottomSheet
            id="db-item-preview"
            title={peekItem?.title || 'Item Preview'}
            isOpen={peekOpen}
            onClose={handlePeekClose}
          >
            {peekContent}
          </PeekBottomSheet>
        ) : (
          <PeekContainer
            id="db-item-preview"
            title={peekItem?.title || 'Item Preview'}
            isOpen={peekOpen}
            onClose={handlePeekClose}
            width={400}
            minWidth={320}
            maxWidth={720}
            topBar={
              peekItem ? (
                <PeekTopBar
                  title={peekItem.title}
                  shareUrl={peekShareUrl}
                  starred={resolveStarred(peekItem)}
                  onExpand={handlePeekExpand}
                  onToggleStar={() => setItemStarred(peekItem.id, !resolveStarred(peekItem))}
                  onDelete={() => handleDeleteItem(peekItem.id)}
                />
              ) : null
            }
          >
            {peekContent}
          </PeekContainer>
        )}

        {/* Toolbar — single compact row on mobile, stacked on desktop. */}
        <Paper
          className="fc-toolbar-surface fc-toolbar"
          p={isMobile ? 0 : 'md'}
          mb={isMobile ? 'xs' : 'md'}
          radius="md"
          style={{ backgroundColor: 'var(--mantine-color-body)' }}
        >
          {isMobile ? (
            /* Mobile: one row, 40px tall, every element shrunk. */
            <Group justify="space-between" align="center" wrap="nowrap" gap={6} px={8} h={40}>
              <Button
                variant="filled"
                color="blue"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => setIsNewItemOpen(true)}
                style={{ minWidth: 0, paddingLeft: 8, paddingRight: 10 }}
              >
                New
              </Button>
              {/* F 280 - THE TAB BAR. This is what F 135 built and nothing switched on.
               *
               * `ViewSwitcher` renders `role="tablist"` / `role="tab"` / `data-fc-view-tab`
               * the moment it is handed `active` + `onChange` (ViewSwitcher.tsx:159). Until
               * now both call sites passed only `activeView` + `onSelect`, so `simpleMode`
               * was false and that branch never executed - the operator got a dropdown.
               *
               * THE CHOICE, per Item 2(c): the tab bar sits ALONGSIDE the dropdown, not
               * instead of it. The dropdown is the only home of the four controls - create,
               * rename, delete and kanban group-by - and F 141 C deliberately rebuilt its
               * "Add a view" list as ten NAMED items with descriptions. Flattening that
               * into a tab row would undo F 141 C.
               *
               * So: tabs for switching, dropdown for managing. The dropdown below is
               * otherwise untouched - a two-line change per site, not a mode change
               * inside ViewSwitcher.
               */}
              {/* F 473 — THE SCROLLING TAB STRIP.
               *
               * The mobile row is `wrap="nowrap"` and `h={40}`, so the tab bar
               * cannot wrap (it used to, and the first tab was clipped off the
               * top of the toolbar while the rest spilled over the FilterRow).
               * `ViewSwitcher` is now `nowrap` + `overflowX: auto`, so it needs
               * a PARENT that gives it the leftover width and lets it scroll.
               *
               * `flex: 1` + `minWidth: 0` is what makes that possible: without
               * `minWidth: 0` a flex child refuses to shrink below its content
               * width, the row overflows the phone, and the scroll is never
               * reachable.
               */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
              <ViewSwitcher
                views={views}
                active={activeView?.id}
                onChange={setActiveViewId}
                // F 299 - THE `+ Add` CONTROL, WIRED. `+ Add` lives in the F 135
                // SIMPLE branch (ViewSwitcher.tsx:236) and renders only when
                // `onAdd || onCreate` is truthy. This site passed neither, so the
                // control did not render AT ALL - verified by probe, not by reading.
                // It was not "dead code that does nothing when clicked"; it was
                // absent from the product, in both the desktop and mobile toolbars.
                //
                // `onAdd`, not `onCreate`: `onCreate` opens the ten-named-item
                // "Add a view" list that F 141 C built, and a bare tab-bar button
                // must not silently pick a layout. `createView('list')` is the same
                // default ViewSwitcher's own fallback already uses
                // (`else if (onCreate) onCreate('list')`).
                onAdd={() => createView('list')}
              />
              </div>
              {/* F 497 — THE PHONE TOOLBAR SHOWS THE SAME NUMBER AS THE DESKTOP
                  ONE. It used to render `viewItems.length` in a Badge, which for
                  a paged database is the PAGE that was read (60), not the
                  database (16,783). */}
              <Badge variant="light" size="sm" data-fc-item-count="">
                {itemCountLabel}
              </Badge>
              {/* F 497 — THE READ-ONLY NOTICE IS VISIBLE ON THE PHONE TOO, not a
                  silent no-op. A control that does nothing must say why. */}
              {isPagedDatabase ? (
                <Text size="xs" c="dimmed" data-fc-paged-notice="" style={{ whiteSpace: 'nowrap' }}>
                  Paged — read only
                </Text>
              ) : null}
              <ActionIcon variant="subtle" size="sm" onClick={() => setMobileSearchOpen(true)} title="Search" aria-label="Search">
                <IconSearch size={14} />
              </ActionIcon>
              <Menu position="bottom-end" shadow="md" width={180}>
                <Menu.Target>
                  <ActionIcon variant="subtle" size="sm" title="More" aria-label="More">
                    <IconDots size={14} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item leftSection={<IconDownload size={14} />} onClick={handleExport}>Export</Menu.Item>
                  <Menu.Item leftSection={<IconSettings size={14} />} onClick={() => setIsPropertiesOpen(true)}>Properties</Menu.Item>
                  {/* F 473 - THE VIEW-MANAGEMENT MENU, ON THE PHONE.
                   *
                   * F 280's rule is "tabs for switching, dropdown for managing",
                   * and that still holds - but on a 390px row the two could not
                   * share the space. Measured: the tab strip occupied
                   * x=99..164 and the dropdown rendered at x=164 with width 116,
                   * so the screenshot showed a truncated "Galeri" tab next to a
                   * full "Gallery" dropdown.
                   *
                   * The tab row already has a "More" menu, so the view manager
                   * goes there as a Popover. The BODY is `ViewSwitcherBody` -
                   * the same component the desktop renders inside its
                   * `Menu.Dropdown` - so rename / delete / group-by /
                   * add-a-view stay ONE implementation (F 141 C's named items
                   * with descriptions included).
                   */}
                  <Menu.Item
                    leftSection={<IconLayoutGrid size={14} />}
                    onClick={(e) => { e.stopPropagation(); setMobileViewsOpen(true); }}
                    rightSection={<IconChevronRight size={14} />}
                  >
                    Views
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          ) : (
            <Group justify="space-between" align="center" wrap="nowrap" gap="md">
              <Group gap="sm" wrap="nowrap">
                <Button
                  variant="filled"
                  color="blue"
                  leftSection={<IconPlus size={16} />}
                  onClick={() => setIsNewItemOpen(true)}
                >
                  New
                </Button>
                {/* F 280 - THE TAB BAR. This is what F 135 built and nothing switched on.
                 *
                 * `ViewSwitcher` renders `role="tablist"` / `role="tab"` / `data-fc-view-tab`
                 * the moment it is handed `active` + `onChange` (ViewSwitcher.tsx:159). Until
                 * now both call sites passed only `activeView` + `onSelect`, so `simpleMode`
                 * was false and that branch never executed - the operator got a dropdown.
                 *
                 * THE CHOICE, per Item 2(c): the tab bar sits ALONGSIDE the dropdown, not
                 * instead of it. The dropdown is the only home of the four controls - create,
                 * rename, delete and kanban group-by - and F 141 C deliberately rebuilt its
                 * "Add a view" list as ten NAMED items with descriptions. Flattening that
                 * into a tab row would undo F 141 C.
                 *
                 * So: tabs for switching, dropdown for managing. The dropdown below is
                 * otherwise untouched - a two-line change per site, not a mode change
                 * inside ViewSwitcher.
                 */}
                <ViewSwitcher
                  views={views}
                  active={activeView?.id}
                  onChange={setActiveViewId}
                  // F 299 - THE `+ Add` CONTROL, WIRED. `+ Add` lives in the F 135
                  // SIMPLE branch (ViewSwitcher.tsx:236) and renders only when
                  // `onAdd || onCreate` is truthy. This site passed neither, so the
                  // control did not render AT ALL - verified by probe, not by reading.
                  // It was not "dead code that does nothing when clicked"; it was
                  // absent from the product, in both the desktop and mobile toolbars.
                  //
                  // `onAdd`, not `onCreate`: `onCreate` opens the ten-named-item
                  // "Add a view" list that F 141 C built, and a bare tab-bar button
                  // must not silently pick a layout. `createView('list')` is the same
                  // default ViewSwitcher's own fallback already uses
                  // (`else if (onCreate) onCreate('list')`).
                  onAdd={() => createView('list')}
                />
                <ViewSwitcher
                  views={views}
                  activeView={activeView}
                  onSelect={setActiveViewId}
                  onCreate={(type) => createView(type)}
                  onRename={renameView}
                  onDelete={deleteView}
                  onChangeGroupBy={handleChangeGroupBy}
                  boardOptions={activeView ? {
                    wrapContent: activeView.wrapContent,
                    colorColumns: activeView.colorColumns,
                    showIcons: activeView.showIcons,
                  } : undefined}
                  onChangeBoardOptions={handleChangeBoardOptions}
                  schema={db.schema.properties}
                  groupByField={activeView?.groupByField}
                  groupGranularity={activeView?.groupGranularity}
                  onChangeDateGrouping={handleChangeDateGrouping}
                />
              </Group>
              {/* F 377 — SEARCH: icon until asked, then expand.
                  *
                  * The icon and the input are the SAME control in two states.
                  * The wrapper animates `width`, and because this whole group is
                  * `flex: 1` with `justifyContent: center`, the count and the
                  * settings icon to the right REFLOW leftwards as the field grows.
                  * That shift is the flexbox doing its job — there is no second
                  * animation and no absolute positioning keeping it in sync.
                  *
                  * Collapse rules, both deliberate:
                  *   - Escape always collapses and blurs.
                  *   - Blur collapses ONLY when the query is empty. A non-empty
                  *     query must survive losing focus, or the user loses the
                  *     text they just typed and cannot see what they are editing.
                  */}
              <Group gap="xs" wrap="nowrap" style={{ flex: 1, justifyContent: 'center', maxWidth: 480 }}>
                <Box
                  data-fc-search-slot=""
                  style={{
                    width: isSearchOpen ? 260 : 34,
                    transition: 'width 180ms cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  {isSearchOpen ? (
                    <TextInput
                      data-fc-search-input=""
                      autoFocus
                      placeholder="Search items…"
                      leftSection={<IconSearch size={16} />}
                      value={globalSearch}
                      onChange={(e) => setGlobalSearch(e.currentTarget.value)}
                      onKeyDown={(e) => { if (e.key === 'Escape') { setIsSearchOpen(false); e.currentTarget.blur(); } }}
                      onBlur={() => { if (globalSearch.length === 0) setIsSearchOpen(false); }}
                      style={{ width: '100%' }}
                    />
                  ) : (
                    <Tooltip label="Search items">
                      <ActionIcon
                        data-fc-search-btn=""
                        variant="light"
                        size="lg"
                        aria-label="Search items"
                        onClick={() => setIsSearchOpen(true)}
                      >
                        <IconSearch size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Box>
                {/* F 375 — the funnel icon that used to live HERE is gone. The
                    filters now have a persistent row of their own, directly below
                    this toolbar (see `<FilterRow …>` after `</Paper>`). The button
                    that opens this popover is the `+ Filter` pill in that row.
                    The popover BODY is unchanged and is now rendered through
                    `filterPopoverBody` — same properties, same checkboxes, same
                    `setPropertyFilter` writes. Only the trigger moved. */}
              </Group>
              <Group gap="xs" wrap="nowrap">
                {/* F 377 — the count was a `Badge` with a 1000px radius: a lozenge
                    that clipped "641 ITE…" in the audit. The word "items" is what
                    made it wide. The toolbar already says what this number is, so
                    it is now bare small grey text with no background — the same
                    treatment Notion gives it. */}
                <Text size="xs" c="dimmed" data-fc-item-count="" style={{ whiteSpace: 'nowrap' }}>
                  {/* F 497 — A PAGED DATABASE'S COUNT IS ITS TOTAL, not the page
                      that happens to be on screen. The page is what was READ;
                      the total is what the database OWNS. */}
                  {itemCountLabel}
                </Text>
                {/* F 497 — THE READ-ONLY NOTICE IS VISIBLE, NOT A SILENT NO-OP.
                    A control that does nothing must say why. */}
                {isPagedDatabase ? (
                  <Text size="xs" c="dimmed" data-fc-paged-notice="" style={{ whiteSpace: 'nowrap' }}>
                    Paged — read only
                  </Text>
                ) : null}
                {/* F 377 — the Export icon LEFT the main toolbar and moved into
                    the settings dialog, where Notion keeps it. It is still
                    reachable: see `onExport` on the <Modal> below. Removing it
                    without re-homing it would have been a regression. */}
                <Tooltip label="Properties">
                  <ActionIcon variant="light" size="lg" onClick={() => setIsPropertiesOpen(true)} aria-label="Properties">
                    <IconSettings size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>
          )}
        </Paper>

        {/* F 473 — THE MOBILE VIEW MANAGER.
         *
         * A Popover, not a nested Menu: `ViewSwitcherBody` renders `Menu.Item`
         * children, and Mantine does not allow a `Menu` inside a `Menu.Dropdown`.
         * The Popover is the shell; the body is the shared component the
         * desktop also renders.
         *
         * It lives OUTSIDE the toolbar `Paper` on purpose — the mobile toolbar
         * row is `h={40}` and `nowrap`, and a popover anchored inside it
         * inherits that clipping.
         */}
        {isMobile ? (
          /* F 473 — A `Menu`, NOT a `Popover`.
           *
           * The first attempt hosted `ViewSwitcherBody` in a `Popover` and it
           * THREW in the browser: `Error: Menu component was not found in the
           * tree`. Mantine's `Menu.Item` requires a `Menu` ancestor — the same
           * hazard `BlockSealModal.tsx` documents ("handing `data-testid` to a
           * `Menu.Item` and getting a runtime throw").
           *
           * So the body keeps its `Menu` ancestor and only the TRIGGER moves:
           * a `Menu.Target` that IS the More-menu item, plus a `Menu.Dropdown`
           * that carries the body. One body, one Menu, no duplication.
           */
          <Menu
            shadow="md"
            width={300}
            position="bottom-end"
            opened={mobileViewsOpen}
            onChange={setMobileViewsOpen}
            closeOnItemClick={false}
            zIndex={1200}
          >
            <Menu.Target>
              {/* A zero-size anchor: the VISIBLE trigger is the More-menu item,
                  which opens this menu. */}
              <span style={{ display: 'none' }} aria-hidden="true" />
            </Menu.Target>
            <Menu.Dropdown mah="min(60vh, 460px)" p={0}>
              <MobileViewBody
                views={views}
                activeView={activeView ?? views[0]}
                onSelect={(id) => { setActiveViewId(id); setMobileViewsOpen(false); }}
                onCreate={mobileCreateView}
                onRename={renameView}
                onDelete={deleteView}
                onChangeGroupBy={handleChangeGroupBy}
                boardOptions={activeView ? {
                  wrapContent: activeView.wrapContent,
                  colorColumns: activeView.colorColumns,
                  showIcons: activeView.showIcons,
                } : undefined}
                onChangeBoardOptions={handleChangeBoardOptions}
                schema={db.schema.properties}
                groupByField={activeView?.groupByField}
                groupGranularity={activeView?.groupGranularity}
                onChangeDateGrouping={handleChangeDateGrouping}
                onDismiss={() => setMobileViewsOpen(false)}
              />
            </Menu.Dropdown>
          </Menu>
        ) : null}

        {/* F 375 — THE PERSISTENT FILTER ROW.
            *
            * Sits directly below the tabs toolbar, inside the same Paper, so it
            * reads as one surface with a rule between the two rows. It reads
            * `propertyFilter` — the object F 280 persists per view — and every
            * pill and the `+ Filter` button write back through `setPropertyFilter`.
            * There is no second store and no local copy: the row is a view over
            * the live filter, so it cannot claim a filter the engine is not
            * actually applying.
            */}
        <FilterRow
          filter={propertyFilter}
          onChange={setPropertyFilter}
          properties={allProperties}
          activeCount={activePropertyFilterCount}
          onClearAll={() => setPropertyFilter({})}
          renderPopoverContent={() => filterPopoverBody}
        />

        {/* Views — driven by the view config registry.
            Every view receives the same selector-shaped items so sorting +
            grouping are consistent. */}
        <div style={{ width: '100%' }}>
          {!activeView ? (
            <Group justify="center" py="xl">
              <Text c="dimmed" size="sm">Loading views…</Text>
            </Group>
          ) : (
            <>
          {fcrLayout === 'gallery' && (
            <GalleryView
              database={db}
              items={viewItems}
              coverPropertyId={activeView.coverPropId}
              onItemSelect={handleItemSelect}
              onItemCreate={handleAddItem}
              /* F 139 — the gallery's "Add item" placeholder now opens the SAME
                 modal as this toolbar's "New" button, instead of the view's own
                 private create modal. One create flow, one dialog. */
              onItemCreateRequest={() => setIsNewItemOpen(true)}
              onItemUpdate={handleUpdateItem}
              onItemDelete={handleDeleteItem}
              onItemDuplicate={handleDuplicateItem}
              onPropertyChange={handlePropertyChange}
            />
          )}

          {fcrLayout === 'kanban' && (
            <KanbanView
              database={db}
              items={viewItems}
              groupBy={activeView.groupBy ?? 'status'}
              /* F 509 — WHICH DATE PROPERTY, AND HOW COARSELY. Both come from
                 the ACTIVE VIEW, so they are PER-VIEW and they survive a
                 reload because they were persisted on the view record. */
              groupByField={activeView.groupByField}
              groupGranularity={activeView.groupGranularity}
              /* F 506 — THE TOGGLES COME FROM THE ACTIVE VIEW, so they are
                 PER-VIEW and they survive a reload because they were
                 persisted on the view record. */
              wrapContent={activeView.wrapContent ?? false}
              colorColumns={activeView.colorColumns ?? true}
              showIcons={activeView.showIcons ?? true}
              onItemSelect={handleItemSelect}
              onItemCreate={handleAddItem}
              onItemCreateInColumn={handleCreateItemInColumn}
              onItemUpdate={handleUpdateItem}
              onItemDelete={handleDeleteItem}
              onItemDuplicate={handleDuplicateItem}
              onPropertyChange={handlePropertyChange}
            />
          )}

          {fcrLayout === 'list' && (
            <ListView
              database={db}
              items={viewItems}
              onItemSelect={handleItemSelect}
              onItemUpdate={handleUpdateItem}
              onItemDelete={handleDeleteItem}
              onItemDuplicate={handleDuplicateItem}
            />
          )}

          {/* F 141 C — the four layouts F 136 SHIPPED had no render branch at
              all. They were registered in VIEW_LAYOUTS and the switcher could
              create them, but DatabaseView only mapped six layouts, so
              selecting a table/dense/card/detail view rendered NOTHING. That
              is why they looked "missing" in the dropdown. */}
          {fcrLayout === 'card' && (
            <CardStackRenderer
              items={fcrRows}
              viewConfig={fcrViewConfig}
              propertyDefs={db.schema.properties}
              onSelect={(id) => {
                const it = viewItems.find((i) => i.id === id);
                if (it) handleItemSelect(it);
              }}
            />
          )}

          {fcrLayout === 'table' && (
            <TableLayoutRenderer
              items={fcrRows}
              viewConfig={fcrViewConfig}
              propertyDefs={db.schema.properties}
            />
          )}

          {fcrLayout === 'dense' && (
            <DenseLayoutRenderer
              items={fcrRows}
              viewConfig={fcrViewConfig}
              propertyDefs={db.schema.properties}
            />
          )}

          {fcrLayout === 'detail' && fcrRows[0] && (
            <DetailLayoutRenderer
              item={fcrRows[0]}
              viewConfig={fcrViewConfig}
              propertyDefs={db.schema.properties}
            />
          )}

          {fcrLayout === 'calendar' && (
            <CalendarView
              database={db}
              items={viewItems}
              datePropertyId={
                activeView.datePropId ??
                db.schema.properties.find(p => p.type === 'date')?.id
              }
              onItemSelect={handleItemSelect}
              onItemUpdate={handleUpdateItem}
              onItemDelete={handleDeleteItem}
              onItemCreate={handleAddItem}
            />
          )}

          {fcrLayout === 'timeline' && (
            <TimelineView
              database={db}
              items={viewItems}
              datePropertyId={
                activeView.datePropId ??
                db.schema.properties.find(p => p.type === 'date')?.id
              }
              onItemSelect={handleItemSelect}
            />
          )}

          {fcrLayout === 'recent' && (
            <RecentView
              database={db}
              items={viewItems}
              onItemSelect={handleItemSelect}
              onItemUpdate={handleUpdateItem}
              onItemDelete={handleDeleteItem}
              onItemDuplicate={handleDuplicateItem}
              onPropertyChange={handlePropertyChange}
            />
          )}
            </>
          )}
        </div>

        {/* F 452 — THE SENTINEL, THE CACHED NOTICE, AND THE BUTTON.
         *
         * The dispatch asks for "load 50 items, scroll, load the next 50".
         * The scroll is handled by the observer above; this block is what the
         * operator SEES while it happens.
         *
         * The button is not a fallback nobody uses — it is the same read, for
         * a browser without IntersectionObserver, for a keyboard, and for an
         * operator who would rather ask than wait. F 486's HQ surface made
         * the same choice ("a button, not a scroll and not a paginator: the
         * list grows in place and the URL never changes"), and it is right:
         * the list grows, the URL does not move, and the position does not
         * jump.
         */}
        {isPagedDatabase ? (
          <Box mt="md" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            {pagedFromCache ? (
              <Text size="xs" c="dimmed" data-fc-paged-cached="">
                Showing cached data — the graph could not be reached.
              </Text>
            ) : null}
            {pagedLoadingMore ? (
              <Text size="xs" c="dimmed" data-fc-paged-loading-more="">
                Loading more…
              </Text>
            ) : null}
            {pagedHasMore ? (
              <Button
                variant="subtle"
                size="xs"
                onClick={() => { void loadMorePagedItems(); }}
                data-fc-paged-load-more=""
              >
                Load more
              </Button>
            ) : null}
            <div ref={sentinelRef} data-fc-paged-sentinel="" style={{ height: 1, width: '100%' }} />
          </Box>
        ) : null}

        {/* New Item Modal */}
        <Modal
          // F 137 DEFECT D: the sticky header (z-index 1100) painted over
          // this modal. FCr's <Modal> defaults to 9999; Mantine's does not.
          zIndex={10000}
          opened={isNewItemOpen}
          onClose={() => {
            setIsNewItemOpen(false);
            setNewItemTitle('');
          }}
          title="Create New Item"
          size="sm"
        >
          <Stack>
            <TextInput
              label="Item Title"
              placeholder="Enter item title"
              value={newItemTitle}
              onChange={(e) => setNewItemTitle(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddItem();
              }}
              autoFocus
            />
            <Group justify="flex-end" gap="sm">
              <Button
                variant="subtle"
                onClick={() => {
                  setIsNewItemOpen(false);
                  setNewItemTitle('');
                }}
              >
                Cancel
              </Button>
              {/* ⇒⇒□ ⇒⇒□ ⇒⇒□ THE SURFACE's OWN MODAL BUILDS NO ITEM, SO
                ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ IT PASSES NONE — the handler builds one from
                ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `newItemTitle`. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□
                ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ AND IT IS WRAPPED, BECAUSE `onClick`
                ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ WOULD PASS THE MOUSE EVENT AS THE ITEM.**
                ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THAT IS THE SECOND HALF
                ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ OF THE PRE-EXISTING BUG.** */}
              <Button onClick={() => handleAddItem()} disabled={!newItemTitle.trim()}>
                Create
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Properties Modal */}
        <Modal
          // F 137 DEFECT D: the sticky header (z-index 1100) painted over
          // this modal. FCr's <Modal> defaults to 9999; Mantine's does not.
          zIndex={10000}
          opened={isPropertiesOpen}
          onClose={() => setIsPropertiesOpen(false)}
          title="Database Properties"
          size="md"
          scrollAreaComponent={Paper}
        >
          <PropertyManager
            properties={db.schema.properties}
            onPropertiesChange={handlePropertiesChange}
            onExport={handleExport}
          />

          {/* Kanban grouping is now per-view; use the ViewSwitcher's
              "Group Board by" menu (top toolbar) instead. The legacy global
              groupByProperty Select has been retired. */}
        </Modal>

        {/* Mobile search modal — full-screen search on phones (triggered
            from the toolbar search icon). */}
        <Modal
          // F 137 DEFECT D: the sticky header (z-index 1100) painted over
          // this modal. FCr's <Modal> defaults to 9999; Mantine's does not.
          zIndex={10000}
          opened={mobileSearchOpen}
          onClose={() => setMobileSearchOpen(false)}
          title="Search"
          size="md"
          fullScreen={isMobile}
        >
          <TextInput
            placeholder="Search items..."
            leftSection={<IconSearch size={16} />}
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.currentTarget.value)}
            autoFocus
            size="md"
          />
        </Modal>
      </div>
    </BaseSurface>
  );
};

/**
 * Universal property filter row — one row per filterable property in
 * the Filters popover. Renders checkboxes for each discovered value.
 * Renders text fields for free-form text properties (just a search
 * input that filters by contains). Renders nothing for properties
 * with zero observed values.
 */
const UniversalPropertyFilterRow: React.FC<{
  property: DiscoveredProperty;
  selected: string[];
  onChange: (values: string[]) => void;
}> = ({ property, selected, onChange }) => {
  // For select / status / multi-select / tag: render checkboxes for the
  // top observed values (cap at 8 visible + a "more" indicator).
  if (property.options && property.options.length > 0) {
    const visibleOptions = property.options.slice(0, 8);
    const more = Math.max(0, property.options.length - visibleOptions.length);
    return (
      <Box>
        <Text size="xs" fw={600} c="dimmed" mb={4}>
          {property.name} <Text component="span" c="dimmed">({property.options.length})</Text>
        </Text>
        <Stack gap={4}>
          {visibleOptions.map(opt => {
            const id = String(opt.id);
            const checked = selected.includes(id);
            return (
              <Checkbox
                key={id}
                size="xs"
                color={opt.color}
                label={
                  <Group gap={4} wrap="nowrap">
                    <Box style={{
                      width: 8, height: 8, borderRadius: 2,
                      backgroundColor: opt.color, flexShrink: 0,
                    }} />
                    <Text size="xs">{opt.name}</Text>
                  </Group>
                }
                checked={checked}
                onChange={(e) => {
                  if (e.currentTarget.checked) onChange([...selected, id]);
                  else onChange(selected.filter(v => v !== id));
                }}
              />
            );
          })}
          {more > 0 && (
            <Text size="xs" c="dimmed">+{more} more…</Text>
          )}
        </Stack>
      </Box>
    );
  }
  // Free-form text: render a small text search input (we treat it as a
  // single-value contains filter).
  return (
    <Box>
      <Text size="xs" fw={600} c="dimmed" mb={4}>{property.name}</Text>
      <TextInput
        size="xs"
        placeholder={`Filter by ${property.name.toLowerCase()}…`}
        value={selected[0] || ''}
        onChange={(e) => onChange(e.currentTarget.value ? [e.currentTarget.value] : [])}
      />
    </Box>
  );
};

export default DatabaseView;